import axios from 'axios';

import { notifications } from '@mantine/notifications';

import { DocumentDto, DocumentUploadDto, ProjectDto } from '../../api/models';
import { usePostApiProjectsIdDocument } from '../../api/projects/projects.ts';
import DocumentModal from './DocumentModal.tsx';

interface DocumentAddProps {
    project: ProjectDto;
    opened: boolean;
    close: () => void;
    refetch: () => void;
    onDocumentCreated: (document: DocumentDto) => void;
    includeFileImport?: boolean;
}

const DocumentAddModal = ({
    opened,
    close,
    project,
    refetch,
    onDocumentCreated,
    includeFileImport
}: DocumentAddProps) => {
    const {
        mutateAsync: addDocument,
        error,
        isPending
    } = usePostApiProjectsIdDocument();

    const handleAdd = async (data: DocumentUploadDto) => {
        try {
            const newDocument = await addDocument({ id: project.id, data });

            notifications.show({
                title: 'Document Created',
                message: `${newDocument.name} created! ✔️`,
                loading: false,
                withCloseButton: true,
                autoClose: 2000
            });

            onDocumentCreated(newDocument);
        } catch (err) {
            const errorMessage =
                axios.isAxiosError(err) && err.response
                    ? `Server error: ${err.response.status} ${err.response.statusText}`
                    : 'An unexpected error occurred ❌';

            notifications.show({
                title: 'Document Creation Failed',
                message: errorMessage,
                loading: false,
                withCloseButton: true,
                autoClose: 5000,
                color: 'red'
            });
        }
    };

    return (
        <DocumentModal
            opened={opened}
            onClose={close}
            onSubmit={handleAdd}
            error={error}
            isPending={isPending}
            refetch={refetch}
            project={project}
            includeFileImport={includeFileImport}
            title={
                includeFileImport
                    ? `Import a New Markdown File to ${project.name}`
                    : `Add a New Document to ${project.name}`
            }
        />
    );
};

export default DocumentAddModal;
